<template>
  <div class="email-domain-settings">
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row">
      <div class="col">
        <!-- Main Card -->
        <card>
          <!-- Sender Configuration -->
          <div class="row">
            <div class="col">
              <p class="card-title text-uppercase mb-2">Sender configuration</p>
            </div>
            </div>
            <div class="row">
              <div class="col-lg-3">
                <label class="text-white">
                  Sender name
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">
                        Displays the name associated with the email account <br/> of the individual or entity sending the message.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info ml-1"></i>
                  </el-popover>
                </label>
                <base-input
                  :error="errors.sender_name"
                  v-model="senderName"
                  placeholder="Off The Couch">
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-3">
                <label class="text-white">
                  Reply to email
                  <el-popover trigger="hover" placement="right">
                    <div>
                      <div class="popover-body">
                        Specifies the email address to which responses should be sent, <br/>differing from the sender's address if necessary.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info ml-1"></i>
                  </el-popover>
                </label>
                <base-input
                  :error="errors.reply_to_email"
                  v-model="replyToEmail"
                  placeholder="info@offthecouch.io">
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <base-button
                  type="primary"
                  size="sm"
                  v-on:click="saveSenderSettings()">
                  <i class="tim-icons icon-check-2 mr-1"></i> Save sender settings
                </base-button>
              </div>
            </div>

            <!-- Email Identity -->
            <div class="row mt-4">
              <div class="col">
                <p class="card-title text-uppercase mb-2">Email identity</p>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-8">
                <info-hint variant="primary">
                  Email identity allows you to configure which domain your automated emails are sent from by default.
                  <strong>Note:</strong> Using a custom domain is completely optional.
                  The default domain (info@offthecouch.io) works perfectly for all automated emails.
                  Custom domains are useful if you want emails to appear to come from your own domain for branding consistency.
                </info-hint>
              </div>
            </div>
            <!-- Current Status -->
            <label class="text-white opacity-8 mt-3">Currently sending from:</label>

            <div class="row mt-2">
              <div class="col-lg-4">
                <div class="current-status-box">
                  <div class="status-value">
                    <code>{{ currentFromEmail }}</code>
                    <badge type="success" v-if="isUsingDefault || domainVerified" class="ml-2">
                      <i class="tim-icons icon-check-2"></i> Verified
                    </badge>
                    <badge type="danger" v-else-if="dnsFailed" class="ml-2">
                      <i class="tim-icons icon-alert-circle-exc"></i> Verification failed
                    </badge>
                    <badge type="warning" v-else class="ml-2">
                      <i class="tim-icons icon-time-alarm"></i> Pending verification
                    </badge>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-if="dnsFailed">
              <div class="col-lg-8">
                <info-hint icon="icon-alert-circle-exc" variant="danger">
                  <strong>Your domain's DNS records are no longer detected.</strong> Automated emails fall back to the default sender and email campaigns are paused until the records are restored and verified.
                  <a v-if="!showSetup && !showRecordsOnly" href="#" @click.prevent="showRecordsOnly = true">View DNS records</a>
                </info-hint>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col">
                <base-button
                  v-if="!showSetup && isUsingDefault"
                  type="primary"
                  size="sm"
                  v-on:click="showSetup = true">
                  <i class="tim-icons icon-settings mr-1"></i> Configure custom domain
                </base-button>

                <div v-if="!isUsingDefault && !showSetup">
                  <base-button
                    v-if="!domainVerified"
                    type="info"
                    size="sm"
                    v-on:click="checkDomainStatus()">
                    <i class="tim-icons icon-refresh-02 mr-1"></i> Check verification status
                  </base-button>
                  <base-button
                    type="default"
                    size="sm"
                    class="ml-2"
                    v-on:click="changeDomain()">
                    <i class="tim-icons icon-pencil mr-1"></i> Change domain
                  </base-button>
                  <base-button
                    link
                    type="primary"
                    size="sm"
                    v-on:click="resetToDefault()">
                    Switch to default
                  </base-button>
                </div>
              </div>
            </div>
            <div v-if="showSetup || showRecordsOnly" class="mt-3">
              <!-- Step 1: Enter Domain -->
              <card class="step-card" v-if="!showRecordsOnly">
                <div class="step-header">
                  <div class="step-number">1</div>
                  <div class="step-title">
                    <h4>Enter Your Email Domain</h4>
                    <p class="text-white opacity-7 mb-0">Choose the email address that will appear as the sender</p>
                  </div>
                </div>

                <div class="step-content">
                  <div class="row">
                    <div class="col-lg-6">
                      <base-input
                        :error="errors.from_email"
                        v-model="newFromEmail"
                        placeholder="noreply@yourdomain.com"
                        label="From Email Address">
                      </base-input>
                    </div>
                  </div>

                  <div class="row mt-3">
                    <div class="col-lg-8">
                      <div class="instruction-box">
                        <h5 class="mb-2"><i class="tim-icons icon-bulb-63"></i> Best Practices</h5>
                        <ul class="mb-0" style="font-size: 0.875rem;">
                          <li>Use a dedicated email like <code>noreply@</code>, <code>notifications@</code>, or <code>automated@</code></li>
                          <li>Avoid using personal email addresses</li>
                          <li>Make sure you own the domain (the part after @)</li>
                          <li>The email doesn't need to be a real mailbox - it's just for display</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="mt-3">
                    <base-button
                      type="primary"
                      size="sm"
                      v-on:click="confirmDomain()"
                      :disabled="!newFromEmail">
                      Confirm and continue
                    </base-button>
                    <base-button
                      type="default"
                      size="sm"
                      class="ml-2"
                      v-on:click="cancelSetup()">
                      Cancel
                    </base-button>
                  </div>
                </div>
              </card>

              <!-- Step 2: DNS Configuration -->
              <card class="step-card" v-if="dnsRecords.length > 0">
                <div class="step-header">
                  <div class="step-number">{{ showRecordsOnly ? 1 : 2 }}</div>
                  <div class="step-title">
                    <h4>Configure DNS Records</h4>
                    <p class="text-white opacity-7 mb-0">Add these records to your domain's DNS settings</p>
                  </div>
                </div>

                <div class="step-content">
                  <!-- Detailed Instructions -->
                  <div class="instruction-box">
                    <h5 class="mb-2"><i class="tim-icons icon-notes"></i> Step-by-Step Instructions</h5>

                    <div class="provider-instructions">
                      <h6 class="mt-2 mb-1" style="font-size: 0.875rem;">1. Access Your DNS Provider</h6>
                      <p class="mb-1" style="font-size: 0.875rem;">Log into your domain registrar or DNS management panel. Common providers include:</p>
                      <ul style="font-size: 0.875rem; margin-bottom: 0.5rem;">
                        <li><strong>GoDaddy:</strong> My Products → DNS → Manage Zones</li>
                        <li><strong>Cloudflare:</strong> Select Domain → DNS Records</li>
                        <li><strong>Namecheap:</strong> Domain List → Manage → Advanced DNS</li>
                        <li><strong>Google Domains:</strong> My Domains → DNS</li>
                      </ul>

                      <h6 class="mt-2 mb-1" style="font-size: 0.875rem;">2. Add Each Record Below</h6>
                      <p class="mb-1" style="font-size: 0.875rem;">For each record in the table below:</p>
                      <ul style="font-size: 0.875rem; margin-bottom: 0.5rem;">
                        <li>Click "Add Record" or similar in your DNS panel</li>
                        <li>Select the record type (CNAME or TXT)</li>
                        <li>Copy and paste the Name and Value exactly as shown</li>
                        <li>Leave TTL as default (usually 3600 or 1 hour)</li>
                      </ul>

                      <h6 class="mt-2 mb-1" style="font-size: 0.875rem;">3. Important Notes</h6>
                      <ul class="mb-0" style="font-size: 0.875rem;">
                        <li>Some providers automatically add your domain to the end of the "Name" field - check if it's already there</li>
                        <li>DNS changes take 24-48 hours to fully propagate worldwide</li>
                        <li>All records must be added for verification to succeed</li>
                      </ul>
                    </div>
                  </div>

                  <!-- DNS Records Table -->
                  <div class="dns-table-container mt-3">
                    <h5 class="mb-2" style="font-size: 1rem;">DNS Records to Add</h5>
                    <div class="table-responsive">
                      <table class="table table-dark">
                        <thead>
                        <tr>
                          <th style="width: 80px;">Type</th>
                          <th>Name / Host</th>
                          <th>Value / Points to</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(record, index) in dnsRecords" :key="index">
                          <td>
                            <badge type="default">{{ record.type }}</badge>
                          </td>
                          <td class="dns-value-cell">
                            <div class="d-flex align-items-center">
                              <code class="flex-grow-1 mr-2">{{ record.name }}</code>
                              <base-button
                                link icon
                                size="sm"
                                @click="copyToClipboard(record.name, 'name', index)">
                                <i class="tim-icons text-white icon-single-copy-04"></i>
                              </base-button>
                            </div>
                          </td>
                          <td class="dns-value-cell">
                            <div class="d-flex align-items-center">
                              <code class="flex-grow-1 mr-2">{{ record.value }}</code>
                              <base-button
                                link icon
                                type="primary"
                                size="sm"
                                @click="copyToClipboard(record.value, 'value', index)">
                                <i class="tim-icons text-white icon-single-copy-04"></i>
                              </base-button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Helper Tools -->
                  <div class="instruction-box mt-3">
                    <h5 class="mb-2"><i class="tim-icons icon-support-17"></i> Troubleshooting Tools</h5>
                    <p class="mb-1" style="font-size: 0.875rem;">After adding the records, you can verify they're propagating correctly:</p>
                    <ul class="mb-0" style="font-size: 0.875rem;">
                      <li>
                        <a href="https://dnschecker.org/" target="_blank" class="text-info">
                          DNSChecker.org
                        </a> - Check DNS propagation worldwide
                      </li>
                      <li>
                        <a href="https://mxtoolbox.com/SuperTool.aspx" target="_blank" class="text-info">
                          MXToolbox
                        </a> - Verify MX and TXT records
                      </li>
                      <li>
                        Use command line: <code>nslookup -type=TXT yourdomain.com</code>
                      </li>
                    </ul>
                  </div>
                </div>
              </card>

              <!-- Step 3: Verify -->
              <card class="step-card" v-if="dnsRecords.length > 0">
                <div class="step-header">
                  <div class="step-number">{{ showRecordsOnly ? 2 : 3 }}</div>
                  <div class="step-title">
                    <h4>Verify Domain</h4>
                    <p class="text-white opacity-7 mb-0">Check if DNS records are properly configured</p>
                  </div>
                </div>

                <div class="step-content">
                  <div class="row">
                    <div class="col-lg-8">
                      <div class="instruction-box">
                        <h5 class="mb-2"><i class="tim-icons icon-time-alarm"></i> Before You Verify</h5>
                        <ul class="mb-0" style="font-size: 0.875rem;">
                          <li>Ensure you've added all 5 DNS records</li>
                          <li>Wait at least 15 minutes after adding records</li>
                          <li>If verification fails, wait a few hours and try again</li>
                          <li>Full propagation can take up to 48 hours</li>
                        </ul>
                      </div>

                      <div class="verify-status mt-3" v-if="verificationAttempted">
                        <base-alert :type="domainVerified ? 'success' : 'warning'">
                          <i class="mr-1 tim-icons" :class="domainVerified ? 'icon-check-2' : 'icon-time-alarm'"></i>
                          <span v-if="domainVerified">
                        <strong>Success!</strong> Your domain is verified and active.
                      </span>
                          <span v-else>
                        <strong>Not yet verified.</strong> DNS records are still propagating. Please try again later.
                      </span>
                        </base-alert>
                      </div>

                      <div class="mt-3">
                        <base-button
                          type="primary"
                          size="sm"
                          v-on:click="verifyDomain()"
                          :disabled="verifying">
                      <span v-if="verifying">
                        <i class="tim-icons icon-refresh-02 spinning"></i> Checking...
                      </span>
                          <span v-else>
                        Verify domain
                      </span>
                        </base-button>

                        <base-button
                          v-if="domainVerified"
                          type="success"
                          size="sm"
                          class="ml-2"
                          v-on:click="completeSetup()">
                          <i class="mr-1 tim-icons icon-check-2"></i> Complete Setup
                        </base-button>
                      </div>
                    </div>
                  </div>
                </div>
              </card>
            </div>

          <!-- Sending health (hidden while the domain setup flow is open) -->
          <template v-if="!showSetup && !showRecordsOnly">
          <div class="row mt-4">
            <div class="col">
              <p class="card-title text-uppercase mb-2">Sending health</p>
            </div>
          </div>
          <div>
            <template v-if="healthTotals">
              <template v-if="healthTotals.sends > 0">
                <div class="row gi-equal-height gi-equal-height--nowrap">
                  <div class="col-lg col-sm-6">
                    <div class="gi-section gi-stat-card">
                      <div class="gi-stat-card__icon"><i class="tim-icons icon-send"></i></div>
                      <div class="gi-stat-card__content">
                        <span class="gi-section__label">Sends</span>
                        <div class="gi-stat-value">{{ healthTotals.sends.toLocaleString() }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg col-sm-6">
                    <div class="gi-section gi-stat-card">
                      <div class="gi-stat-card__icon"><i class="tim-icons icon-email-85"></i></div>
                      <div class="gi-stat-card__content">
                        <span class="gi-section__label">Delivery rate</span>
                        <div class="gi-stat-value">{{ healthRates.delivery.toFixed(1) }}%</div>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg col-sm-6">
                    <div class="gi-section gi-stat-card">
                      <div class="gi-stat-card__icon"><i class="tim-icons icon-alert-circle-exc"></i></div>
                      <div class="gi-stat-card__content">
                        <span class="gi-section__label">Bounce rate</span>
                        <div class="gi-stat-value" :class="bounceRateClass">
                          {{ healthRates.bounce.toFixed(1) }}%
                          <small :class="bounceRateClass">{{ bounceStatusText }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg col-sm-6">
                    <div class="gi-section gi-stat-card">
                      <div class="gi-stat-card__icon"><i class="tim-icons icon-bell-55"></i></div>
                      <div class="gi-stat-card__content">
                        <span class="gi-section__label">Complaint rate</span>
                        <div class="gi-stat-value" :class="complaintRateClass">
                          {{ healthRates.complaint.toFixed(1) }}%
                          <small :class="complaintRateClass">{{ complaintStatusText }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg col-sm-6">
                    <div class="gi-section gi-stat-card">
                      <div class="gi-stat-card__icon"><i class="tim-icons icon-simple-remove"></i></div>
                      <div class="gi-stat-card__content">
                        <span class="gi-section__label">Unsubscribe rate</span>
                        <div class="gi-stat-value">{{ healthRates.unsubscribe.toFixed(1) }}%</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mt-3"><small class="text-muted">Last 30 days</small></div>
              </template>
              <p v-else class="text-muted mb-0">No emails sent in the last 30 days.</p>
            </template>
            <p v-else class="text-muted mb-0">Sending health is unavailable right now.</p>
          </div>
          </template>

        </card>

        <!-- Setup Steps (Vertical) -->
      </div>
    </div>
  </div>
</template>

<script>
import {API_LOCATION} from "@/api-config";
import {Auth} from "aws-amplify";
import BaseAlert from "@/components/BaseAlert";
import Badge from "@/components/Badge";
import swal from "sweetalert2";
import {isValidEmail} from "@/util/emailValidator";

export default {
  name: "EmailDomainSettings",
  components: {
    BaseAlert,
    Badge
  },
  computed: {
    dnsFailed() {
      return ['FAILED', 'TEMPORARY_FAILURE'].includes(this.dnsStatus) && !this.domainVerified && !this.isUsingDefault;
    },
    healthRates() {
      if(!this.healthTotals || !this.healthTotals.sends) return null;
      const sends = this.healthTotals.sends;
      return {
        delivery: (this.healthTotals.delivered / sends) * 100,
        bounce: (this.healthTotals.bounces / sends) * 100,
        complaint: (this.healthTotals.complaints / sends) * 100,
        unsubscribe: (this.healthTotals.unsubscribes / sends) * 100
      };
    },
    bounceRateClass() {
      if(!this.healthRates) return '';
      if(this.healthRates.bounce > 5) return 'text-danger';
      if(this.healthRates.bounce >= 2) return 'text-warning';
      return 'text-success';
    },
    complaintRateClass() {
      if(!this.healthRates) return '';
      if(this.healthRates.complaint > 0.5) return 'text-danger';
      if(this.healthRates.complaint >= 0.1) return 'text-warning';
      return 'text-success';
    },
    bounceStatusText() {
      if(this.bounceRateClass === 'text-danger') return '▲ High';
      if(this.bounceRateClass === 'text-warning') return '▲ Elevated';
      return '✓ Healthy';
    },
    complaintStatusText() {
      if(this.complaintRateClass === 'text-danger') return '▲ High';
      if(this.complaintRateClass === 'text-warning') return '▲ Elevated';
      return '✓ Healthy';
    }
  },
  data() {
    return {
      loading: false,
      showSetup: false,
      currentFromEmail: 'info@offthecouch.io',
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      newFromEmail: '',
      domainVerified: false,
      dnsStatus: null,
      showRecordsOnly: false,
      loadedSettings: null,
      isUsingDefault: true,
      settingId: null,
      dnsRecords: [],
      verifying: false,
      verificationAttempted: false,
      senderName: '',
      replyToEmail: '',
      healthTotals: null,
      errors: {
        from_email: '',
        sender_name: '',
        reply_to_email: ''
      }
    }
  },
  methods: {
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken);
          })
          .catch(err => console.log(err));
      });
    },

    isEmailValid(emailString) {
      return isValidEmail(emailString);
    },

    copyToClipboard(text, type, index) {
      navigator.clipboard.writeText(text);
      this.$notify({
        type: 'success',
        message: `Copied ${type} to clipboard`,
        timeout: 2000
      });
    },

    cancelSetup() {
      this.showSetup = false;
      this.showRecordsOnly = false;
      this.newFromEmail = '';
      this.errors.from_email = '';
      this.verificationAttempted = false;
      if (this.isUsingDefault) {
        this.dnsRecords = [];
      } else if (this.loadedSettings && this.loadedSettings.domain) {
        this.populateDnsRecords(this.loadedSettings);
      }
    },

    changeDomain() {
      this.showSetup = true;
      this.showRecordsOnly = false;
      this.newFromEmail = this.currentFromEmail;
      this.dnsRecords = [];
      this.verificationAttempted = false;
    },

    async saveSenderSettings() {
      // Validate inputs
      this.errors.sender_name = '';
      this.errors.reply_to_email = '';

      if(!this.senderName || this.senderName.trim() === '') {
        this.errors.sender_name = 'Sender name is required';
        return;
      }

      if(!this.replyToEmail || !this.isEmailValid(this.replyToEmail)) {
        this.errors.reply_to_email = 'Please enter a valid reply-to email address';
        return;
      }

      this.loading = true;
      const token = await this.getAuthToken();
      const config = {headers: {Authorization: token}};
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/email-settings';
      const data = {
        sender_name: this.senderName,
        reply_to_email: this.replyToEmail
      };

      this.axios.put(url, data, config)
        .then(response => {
          this.loading = false;
          swal('Success!', 'Sender settings updated successfully', 'success')
        })
        .catch(err => {
          this.loading = false;
          console.log(err);
          swal('Error', 'Failed to update sender settings', 'error');
        });
    },

    completeSetup() {
      this.showSetup = false;
      this.showRecordsOnly = false;
      this.newFromEmail = '';
      this.verificationAttempted = false;
      swal('Success!', 'Your custom domain is now active!', 'success');
    },

    async loadSettings() {
      this.loading = true;
      const token = await this.getAuthToken();
      const config = {headers: {Authorization: token}};
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/email-settings';

      this.axios.get(url, config)
        .then(async response => {
          const settings = response.data.settings;
          this.loadedSettings = settings;
          this.settingId = settings.id;
          this.dnsStatus = settings.dns_status;
          this.currentFromEmail = settings.from_email;
          this.senderName = settings.sender_name || '';
          this.replyToEmail = settings.reply_to_email || '';

          if(settings.from_email !== 'info@offthecouch.io') {
            this.isUsingDefault = false;
            this.domainVerified = settings.custom_domain_verified;

            if(settings.domain) {
              this.populateDnsRecords(settings);
            }

            // Check verification status on load
            if(!this.domainVerified) {
              const verified = await this.checkDomainStatusSilent();
              this.domainVerified = verified;
            }
          }

          this.loading = false;
        })
        .catch(err => {
          this.loading = false;
          console.log(err);
          if(err.response && err.response.status === 401) {
            swal('Error', 'You do not have access to this feature.', 'error');
            this.$router.push('/');
          }
        });
    },

    populateDnsRecords(settings) {
      if(!settings.domain) return;

      this.dnsRecords = [
        {
          type: 'CNAME',
          name: `${settings.dkim_token_1}._domainkey.${settings.domain}`,
          value: `${settings.dkim_token_1}.dkim.amazonses.com`
        },
        {
          type: 'CNAME',
          name: `${settings.dkim_token_2}._domainkey.${settings.domain}`,
          value: `${settings.dkim_token_2}.dkim.amazonses.com`
        },
        {
          type: 'CNAME',
          name: `${settings.dkim_token_3}._domainkey.${settings.domain}`,
          value: `${settings.dkim_token_3}.dkim.amazonses.com`
        },
        {
          type: 'TXT',
          name: `_amazonses.${settings.domain}`,
          value: settings.verification_token
        },
        {
          type: 'TXT',
          name: `_dmarc.${settings.domain}`,
          value: 'v=DMARC1; p=none;'
        },
        // {
        //   type: 'TXT',
        //   name: settings.domain,
        //   value: 'v=spf1 include:amazonses.com ~all'
        // },
        // {
        //   type: 'MX',
        //   name: `mail.${settings.domain}`,
        //   value: '10 feedback-smtp.us-east-1.amazonses.com'
        // }
      ];
    },

    async confirmDomain() {
      if(!this.newFromEmail || !this.isEmailValid(this.newFromEmail)) {
        this.errors.from_email = 'Please enter a valid email address';
        return;
      }
      this.errors.from_email = '';

      this.loading = true;
      const token = await this.getAuthToken();
      const config = {headers: {Authorization: token}};
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/email-settings/confirm-custom-domain';
      const data = {
        from_email: this.newFromEmail
      };

      this.axios.post(url, data, config)
        .then(response => {
          const responseData = response.data;
          const settings = {
            domain: responseData.domain,
            dkim_token_1: responseData.dkim_tokens[0],
            dkim_token_2: responseData.dkim_tokens[1],
            dkim_token_3: responseData.dkim_tokens[2],
            verification_token: responseData.verification_token
          };

          this.populateDnsRecords(settings);
          this.currentFromEmail = responseData.from_email;
          this.isUsingDefault = false;
          this.domainVerified = responseData.custom_domain_verified;

          swal('Domain Confirmed', 'Now add the DNS records shown below to your domain settings.', 'info');
          this.loading = false;
        })
        .catch(err => {
          this.loading = false;
          console.log(err);
          swal('Error', 'Failed to configure domain', 'error');
        });
    },

    async verifyDomain() {
      this.verifying = true;
      const token = await this.getAuthToken();
      const config = {headers: {Authorization: token}};
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/email-settings/verify-custom-domain';
      const data = {
        from_email: this.currentFromEmail
      };

      this.axios.post(url, data, config)
        .then(response => {
          this.verificationAttempted = true;
          this.domainVerified = true;
          this.verifying = false;
        })
        .catch(err => {
          this.verificationAttempted = true;
          this.domainVerified = false;
          this.verifying = false;
          console.log(err);
        });
    },

    async checkDomainStatus() {
      this.loading = true;
      const verified = await this.checkDomainStatusSilent();
      this.loading = false;

      if(verified) {
        this.domainVerified = true;
        swal('Verified!', 'Your domain is verified and active!', 'success');
      } else {
        swal('Pending', 'Domain verification is still pending. DNS propagation can take 24-48 hours.', 'info');
      }
    },

    async checkDomainStatusSilent() {
      const token = await this.getAuthToken();
      const config = {headers: {Authorization: token}};
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/email-settings/verify-custom-domain';
      const data = {
        from_email: this.currentFromEmail
      };

      try {
        const response = await this.axios.post(url, data, config);
        return response.data.custom_domain_verified || false;
      } catch(err) {
        console.log(err);
        return false;
      }
    },

    async loadSendingHealth() {
      // Silent on failure: the panel simply does not render
      const token = await this.getAuthToken();
      const config = {headers: {Authorization: token}};
      const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/email-health';

      try {
        const response = await this.axios.get(url, config);
        this.healthTotals = response.data.totals;
      } catch(err) {
        console.log(err);
      }
    },

    async resetToDefault() {
      swal({
        title: 'Switch to default domain?',
        text: 'This will remove your custom domain configuration and use info@offthecouch.io',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, use default'
      }).then(async (result) => {
        if(result.value) {
          this.loading = true;
          const token = await this.getAuthToken();
          const config = {headers: {Authorization: token}};
          const companyId = localStorage.getItem('companyId');
          const url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.companyGroupId + '/email-settings/reset-domain';

          this.axios.put(url, {}, config)
            .then(response => {
              this.currentFromEmail = 'info@offthecouch.io';
              this.isUsingDefault = true;
              this.domainVerified = false;
              this.dnsRecords = [];
              this.showSetup = false;
              this.showRecordsOnly = false;
              swal('Success!', 'Now using default domain', 'success');
              this.loading = false;
            })
            .catch(err => {
              this.loading = false;
              console.log(err);
              swal('Error', 'Failed to reset domain', 'error');
            });
        }
      });
    }
  },
  mounted() {
    this.loadSettings();
    this.loadSendingHealth();
  }
}
</script>

<style scoped>
/* Current Status Box */
.current-status-box {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  text-align: center;
}

.status-label {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8125rem;
  margin-bottom: 0;
}

.status-value {
  font-size: 1rem;
  color: white;
}

.status-value code {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  font-size: 0.9375rem;
}

/* Email Flow Visual */
.email-flow-visual {
  position: relative;
  padding: 2rem 0;
  margin: 2rem 0;
}

.flow-line {
  position: absolute;
  top: 50%;
  left: 15%;
  right: 15%;
  height: 2px;
  background: linear-gradient(90deg,
  rgba(255,255,255,0.1) 0%,
  rgba(225,78,202,0.3) 50%,
  rgba(255,255,255,0.1) 100%);
  z-index: 0;
}

.flow-nodes {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.flow-node {
  text-align: center;
  flex: 1;
}

.node-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.3s;
}

.flow-node.active .node-icon {
  background: rgba(225, 78, 202, 0.1);
  border-color: #e14eca;
  color: #e14eca;
}

.flow-node.custom .node-icon {
  background: rgba(255, 141, 114, 0.1);
  border-color: #ff8d72;
  color: #ff8d72;
}

.node-label {
  font-size: 0.875rem;
  color: white;
  margin-bottom: 0.25rem;
}

.node-detail {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Step Cards */
.step-card {
  margin-bottom: 1.25rem;
  border-left: 3px solid #e14eca;
}

.step-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.step-number {
  width: 36px;
  height: 36px;
  background: #e14eca;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.125rem;
  margin-right: 0.875rem;
  flex-shrink: 0;
}

.step-title h4 {
  margin-bottom: 0.25rem;
  color: white;
  font-size: 1.125rem;
}

.step-content {
  margin-left: 3rem;
}

/* Instruction Box */
.instruction-box {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.875rem 1rem;
}

.instruction-box h5 {
  color: #e14eca;
  margin-bottom: 0.625rem;
  font-size: 0.9375rem;
}

.instruction-box h6 {
  color: white;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

.instruction-box ul {
  margin-bottom: 0;
  padding-left: 1.25rem;
}

.instruction-box li {
  margin-bottom: 0.375rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
}

.instruction-box code {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  font-size: 0.8125rem;
}

/* DNS Table */
.dns-table-container {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  padding: 0.875rem 1rem;
}

.dns-table-container h5 {
  color: white;
  font-size: 0.9375rem;
}

/* Keep the dark table's header legible in dark mode */
.dns-table-container .table-dark th {
  color: rgba(255, 255, 255, 0.7);
}

.dns-value-cell {
  word-break: break-all;
  font-size: 0.8125rem;
}

.dns-value-cell code {
  color: #00bf9a;
  background: rgba(0, 191, 154, 0.1);
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  font-size: 0.75rem;
}

/* Utilities */
.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .flow-line {
    display: none;
  }

  .flow-nodes {
    flex-direction: column;
    gap: 1rem;
  }

  .step-content {
    margin-left: 0;
    margin-top: 1rem;
  }

  .dns-value-cell {
    font-size: 0.75rem;
  }
}
</style>

<style>
/* Shared stat-card primitive (gi-section / gi-stat-card), also used by
   CampaignReport, with its own light-mode counters */
@import '../../Games/GameHistory/game-instance.css';

/* Light mode counters for this page's dark-hardcoded boxes.
   Unscoped so the body .white-content class matches; namespaced under
   .email-domain-settings so nothing leaks app-wide. */
.white-content .email-domain-settings .current-status-box,
.white-content .email-domain-settings .instruction-box {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content .email-domain-settings .dns-table-container {
  background: rgba(0, 0, 0, 0.04);
}

.white-content .email-domain-settings .status-value,
.white-content .email-domain-settings .instruction-box h6,
.white-content .email-domain-settings .dns-table-container h5,
.white-content .email-domain-settings .step-title h4 {
  color: #344675;
}

.white-content .email-domain-settings .instruction-box li {
  color: rgba(0, 0, 0, 0.7);
}

/* Flip the DNS records table to light */
.white-content .email-domain-settings .dns-table-container .table-dark {
  background-color: transparent;
  color: #344675;
}

.white-content .email-domain-settings .dns-table-container .table-dark th,
.white-content .email-domain-settings .dns-table-container .table-dark td {
  background-color: transparent;
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content .email-domain-settings .dns-table-container .table-dark th {
  color: #344675;
}
</style>
